import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { closeModal } from "../../../libs/modals";
import {
  BULK_CREATE_POST_MEDIA_SELECT_MODAL,
  openBulkCreatePostModal,
} from "./bulk-create-post.actions";
import {
  MediaPicker,
  type MediaPickerTab,
} from "../../media-library/components/media-picker/media-picker";
import { useDispatch, useSelector } from "react-redux";
import { ButtonIcon } from "@dialogueconsulting/sked-ui";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../../components/ui/tooltip";
import {
  CreatePostRequest,
  OpenBulkCreatePostModal,
  PostCloseBehavior,
} from "./bulk-create-post.types";
import { convertCollectionFileToPostMediaFile } from "../../media-library/convert-collection-file-to-post-media-file";
import { useCallback, useMemo, useRef, useState } from "react";
import { SelectedMediaFiles } from "../../media-library/components/selected-media-files/selected-media-files";
import { CollectionFile } from "../../../shared/types/files";
import { Post } from "../types";
import { PostFormState } from "../../../shared/types/post";
import { AccountType } from "../../../shared/types/accounts";
import { RequiredU } from "../../../types/utils";
import { useTrackEvent } from "../../../libs/analytics";
import { SELECTED_MEDIA_FROM_LIBRARY_BULK } from "../../../constants/Events";
import { toast } from "react-toastify";
import Bugsnag from "@bugsnag/js";
import {
  getAccountPlatformType,
  isInstagramAccount,
} from "../../../entities/accounts/libs/account-platform";
import { getStoryMediaLimit } from "../../../utils/posts";
import { useCurrentUser } from "../../../entities/current-user";
import type { UserWithEntitlements } from "../../../utils/entitlements-permission";
import { BULK_POSTS_DEFAULT_LIMIT } from "../../../utils/plan-limits";
import { CAROUSEL_POST_LIMIT, getBulkCreateLimit } from "./limit-config";

export interface MediaSelectModalProps {
  initialTab?: MediaPickerTab;
  post?: Partial<Post>;
  postCloseBehavior?: PostCloseBehavior;
  onPostSaved?: OpenBulkCreatePostModal["onPostSaved"];
  form?: Partial<PostFormState>;
}
export const MediaSelectModal = ({
  initialTab,
  post,
  postCloseBehavior,
  onPostSaved,
  form,
}: MediaSelectModalProps) => {
  const dispatch = useDispatch();
  const accountsById = useSelector(
    (state: any) => state.entities.accounts.byId
  ) as Record<string, AccountType>;

  // Ensure post is never undefined to prevent saga crashes
  const safePost = post || {};

  const currentUser = useCurrentUser();
  const storyMediaLimit = getStoryMediaLimit(
    safePost as any,
    ((safePost.accountIds as string[] | undefined) ?? []).map((id) =>
      getAccountPlatformType(accountsById[id])
    )
  );
  // Selection is for Bulk posts / Bulk stories, one post per asset, so it
  // follows the bulk entitlement. Never below master's 20: the bulk saga
  // already trims to the entitlement and tells the customer to upgrade.
  const selectionLimit =
    storyMediaLimit ??
    Math.max(
      getBulkCreateLimit(currentUser as UserWithEntitlements),
      BULK_POSTS_DEFAULT_LIMIT
    );
  const postLimit = storyMediaLimit ?? CAROUSEL_POST_LIMIT;

  const closeThisModal = (
    requests: RequiredU<Omit<CreatePostRequest, "formTitle">>[]
  ) => {
    dispatch(closeModal({ type: BULK_CREATE_POST_MEDIA_SELECT_MODAL }));
    dispatch(
      openBulkCreatePostModal({ requests, postCloseBehavior, onPostSaved })
    );
  };
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const trackEvent = useTrackEvent();
  const [selectedFiles, setSelectedFiles] = useState<CollectionFile[]>([]);
  const footerRef = useRef<{
    handleSubmit?: () => void;
    handleBulkStories?: () => void;
    handleFileRemove?: (file: CollectionFile) => void;
    handleFilesClear?: () => void;
  }>({});

  // Bulk posts support images, videos, and documents.
  // Documents are LinkedIn-only — PostForm handles platform restriction when asset type is "document".
  const validBulkFiles = useMemo(
    () =>
      selectedFiles.filter(
        (file) => file.meta.isImage || file.meta.isVideo || file.meta.isDocument
      ),
    [selectedFiles]
  );

  const handleBulkPostCreation = useCallback(() => {
    if (validBulkFiles.length < 2) {
      toast.error("Not enough compatible files for bulk post creation.");
      return;
    }

    const requests: RequiredU<Omit<CreatePostRequest, "formTitle">>[] = [];
    const failedFiles: string[] = [];

    for (const file of validBulkFiles) {
      try {
        requests.push({
          type: "create",
          assets: [convertCollectionFileToPostMediaFile(file)],
          post: safePost,
          form,
        });
      } catch (error) {
        console.error(`Failed to convert file ${file._id}:`, error);
        Bugsnag.notify(
          error instanceof Error ? error : new Error(String(error))
        );
        failedFiles.push(file.name || file._id);
      }
    }

    if (failedFiles.length > 0) {
      toast.warn(
        `${failedFiles.length} file${failedFiles.length === 1 ? " was" : "s were"} skipped due to conversion errors: ${failedFiles.join(", ")}`
      );
    }

    if (requests.length < 2) {
      toast.error("Not enough valid files remaining for bulk post creation.");
      return;
    }

    closeThisModal(requests);
    trackEvent({
      eventName: SELECTED_MEDIA_FROM_LIBRARY_BULK,
      metadata: { length: requests.length },
    });
  }, [validBulkFiles, closeThisModal, safePost, form, trackEvent]);

  // Parameter kept for interface compatibility; uses memoized validBulkFiles for consistency
  const renderActionButtons = (_files: CollectionFile[]) => {
    return (
      <div ref={tooltipContainerRef}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ButtonIcon
                onClick={handleBulkPostCreation}
                icon={<i className="fa-regular fa-layer-group" />}
                aria-label="Create bulk posts"
                disabled={validBulkFiles.length <= 1}
                className="disabled:bg-white disabled:shadow-none"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="w-[120px] text-xs leading-4 text-center"
          >
            Bulk create multiple posts
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  return (
    <Dialog
      open={true}
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          closeThisModal([{ type: "create", post, form, assets: undefined }]);
        }
      }}
    >
      <DialogContent
        className="w-[90vw] max-w-[1400px] max-h-[90vh] p-0 flex flex-col"
        contentWrapperClassName="px-0"
        showCloseButton={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-neutral-200 flex-shrink-0">
          <DialogTitle className="text-left font-gotham text-lg font-semibold text-neutral-900">
            Select Media
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1 pb-0">
          <MediaPicker
            hideFooter={true}
            onSelectionChange={setSelectedFiles}
            footerRef={footerRef}
            persistState={true}
            hideCloseButton={true}
            /*
             * A story with an Instagram account is still capped at three,
             * DELIBERATELY, while the main composer's cap is lifted (SKE-4567).
             *
             * The lift asks the server whether THIS post is API-expressible, and
             * at this point the post has no media at all: the customer is here to
             * choose it. Answering that question against an empty post would be
             * answering about the wrong shape. Bulk create needs its own
             * approach, and inventing one here would be guessing.
             */
            limit={selectionLimit}
            postLimit={postLimit}
            onClose={() => {
              closeThisModal([
                { type: "create", post: safePost, form, assets: undefined },
              ]);
            }}
            onSelect={async (files) => {
              // Check if this is a bulk stories request
              const isBulkStory = files.some((file: any) => file._isBulkStory);

              // If bulk stories, create individual story posts for each asset
              if (isBulkStory) {
                // Stories are only supported by Instagram accounts — filter out non-IG accounts
                const originalAccountIds =
                  (safePost.accountIds as string[]) || [];
                const igAccountIds = originalAccountIds.filter((id) => {
                  const account = accountsById[id];
                  return isInstagramAccount(account!);
                });

                if (igAccountIds.length === 0) {
                  toast.error(
                    "None of the selected accounts support stories. Please select at least one Instagram account."
                  );
                  return;
                }

                if (igAccountIds.length < originalAccountIds.length) {
                  toast.info(
                    "Only Instagram accounts support stories. Other accounts have been removed."
                  );
                }

                closeThisModal(
                  files.map((file) => ({
                    type: "create",
                    assets: [file],
                    post: {
                      ...safePost,
                      isStory: true,
                      accountIds: igAccountIds,
                    },
                    form,
                  }))
                );
                trackEvent({
                  eventName: SELECTED_MEDIA_FROM_LIBRARY_BULK,
                  metadata: { length: files.length, type: "stories" },
                });
              } else {
                // Regular media selection - single post with all assets
                closeThisModal([
                  {
                    type: "create",
                    assets: files,
                    post: safePost,
                    form,
                  },
                ]);
              }
            }}
            selectedAccountIds={(safePost.accountIds as string[]) ?? []}
            initialTab={initialTab}
            onSkip={() => {
              closeThisModal([
                { type: "create", post: safePost, form, assets: undefined },
              ]);
            }}
            renderActionButtons={renderActionButtons}
          />
        </DialogBody>
        <DialogFooter className="border-t border-neutral-200 flex-shrink-0 relative z-10 bg-white px-6 py-4">
          <SelectedMediaFiles
            limit={selectionLimit}
            selectedMediaFiles={selectedFiles}
            onRemove={(file) => {
              footerRef.current.handleFileRemove?.(file);
            }}
            onClear={() => {
              footerRef.current.handleFilesClear?.();
            }}
            onCancel={() => {
              // Clear state and close modal without opening create post
              footerRef.current.handleFilesClear?.();
              dispatch(
                closeModal({ type: BULK_CREATE_POST_MEDIA_SELECT_MODAL })
              );
            }}
            onSubmit={() => {
              footerRef.current.handleSubmit?.();
            }}
            onBulkPosts={handleBulkPostCreation}
            onBulkStories={() => {
              footerRef.current.handleBulkStories?.();
            }}
            onSkip={() => {
              closeThisModal([
                { type: "create", post: safePost, form, assets: undefined },
              ]);
            }}
            disabled={selectedFiles.length === 0}
            renderActionButtons={renderActionButtons}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
